// Craft Edit Credit Purchase Modal
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { X, Loader2, Sparkles, Check, TrendingUp } from 'lucide-react';

export function CraftCreditPurchaseModal({ isOpen, onClose, onPurchaseComplete }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPackages();
    }
  }, [isOpen]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const data = await api.request('/credits/packages');
      setPackages(data.packages || []);
    } catch (err) {
      console.error('Failed to load packages:', err);
      setError('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    try {
      setPurchasing(true);
      setError('');
      setSelectedPackage(packageId);

      const response = await api.request('/credits/purchase/create-checkout', {
        method: 'POST',
        body: JSON.stringify({ package_id: packageId })
      });

      if (response.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.checkout_url;
      } else {
        setError('Failed to create checkout session');
        setPurchasing(false);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Failed to start purchase');
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              Buy Craft Editing Credits
            </h2>
            <p className="text-gray-600 mt-1">Power up your writing with AI craft editing</p>
          </div>
          <button
            onClick={onClose}
            disabled={purchasing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <Alert className="mb-6 bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className={`relative border-2 transition-all ${
                    pkg.popular
                      ? 'border-purple-600 shadow-lg'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <CardHeader>
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <CardDescription>{pkg.description}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    <div className="mb-4">
                      <div className="text-4xl font-bold text-gray-900">
                        {pkg.credits}
                        <span className="text-lg font-normal text-gray-600"> credits</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mt-2">
                        ${pkg.price}
                      </div>
                      {pkg.discount_percent > 0 && (
                        <div className="text-green-600 font-semibold mt-1">
                          Save {pkg.discount_percent}%
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Perfect for {Math.floor(pkg.credits / 15)} medium edits</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Credits never expire</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Use anytime on any document</span>
                      </div>
                    </div>

                    <Button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasing}
                      className={`w-full ${
                        pkg.popular
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                          : 'bg-purple-600 hover:bg-purple-700'
                      } text-white`}
                    >
                      {purchasing && selectedPackage === pkg.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>Purchase {pkg.credits} Credits</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">What are Craft Editing Credits?</h3>
            <p className="text-sm text-blue-800">
              Credits let you use our premium AI craft editing feature to improve your writing style, 
              depth, sentence structure, and more. Unlike story editing, craft editing focuses on 
              <strong> HOW you write</strong>, not what you write. Perfect for polishing your manuscript 
              to professional quality!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
